import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RouteComponent } from '@app/route/route.component';
import { StateControlParameters, StateService, ZaverProduct } from '@app/service/state/state.service';
import { HeaderService } from '@app/service/header/header.service';
import { UserService } from '@app/service/user-service/user.service';
import { SelectorItem } from '@app/component/selector/selector.component';
import { AisAccountService, AisBankAccount, BankName } from '@app/service/ais-account/ais-account.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';

@Component({
  selector: 'se-instant-debit-in-house',
  templateUrl: './se-instant-debit-in-house.component.html',
  styleUrls: [
    '../../../page-shared.css',
    './se-instant-debit-in-house.component.scss',
  ],
})
export class SeInstantDebitInHouseComponent extends RouteComponent implements OnInit {
  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private aisAccountService: AisAccountService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  directDebitTermsLink: string = 'https://cdn.zaver.com/SE/docs/zaver-se-autogiro-250828.pdf';

  accounts: AisBankAccount[] = null;
  selectedAccount: AisBankAccount = null;
  tempSelectedAccountId: string = null;
  showAccountSelection: boolean = false;
  loading: boolean = true;

  selectorItems: SelectorItem[] = null;
  modalActive: boolean = false;
  selectedItem: SelectorItem;

  async ngOnInit(): Promise<void> {
    // It is expected to have at least one valid AIS account at this point
    await this.loadAccounts();
    this.loading = false;


    this.selectorItems = [
      {
        tab: 'Ditt köp',
        title: 'Sammanfattning',
        descriptions: [{ text: this.state.paymentInformation.description }],
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `Totalbelopp inkl. moms: ${this.getTotalAmount()}`,
      },
      {
        tab: 'Betalningsvillkor',
        title: `Villkor för betalning`,
        descriptions: [
          {
            text: `Betalning via Autogiro sker genom det autogiromedgivande som du vid genomförande av transaktionen lämnar (se under “Medgivande till betalning via aAutogiro” nedan) eller som du tidigare har lämnat Zaver (Frink AB, org.nr 559059-8420) (“Zaver”) , och som gäller för de produkter och tjänster där Zaver erbjuder betalning via Autogiro.`,
          },
          {
            title: 'Tidpunkt för autogirodebitering',
            text: `När du betalar via Autogiro kommer pengarna att dras från ditt konto inom 1-2 bankdagar från det att handlaren bekräftat hela eller delar av köpet till oss.`,
          },
          {
            title: 'Misslyckad autogirodebitering',
            text: 'Skulle täckning saknas på ditt konto när betalningstransaktionen ska genomföras kommer Zaver ställa ut en faktura till dig. Genom att godkänna betalning genom Autogiro godkänner du således att Zaver, vid misslyckad autogirodebitering, ställer ut en faktura till dig. Transaktionen innebär därmed att du utnyttjar ett kreditutrymme.',
          },
          {
            title: 'Avgifter och dröjsmålsränta',
            text: 'Fakturan ska betalas inom den tid som anges på fakturan. Zaver förbehåller sig rätten att ta ut en administrationsavgift på upp till 49 kr på fakturan. Vid sen betalning av fakturan har Zaver rätt att ta ut en påminnelseavgift om 60 kr samt debitera dröjsmålsränta på det förfallna beloppet till dess att full betalning sker. Dröjsmålsräntan utgår med 24 % per år. Sker ingen betalning därefter kan fordran komma att överlämnas för inkassohantering där ytterligare inkassokostnader kan tillkomma.',
          },
          {
            title: 'Kreditprövning',
            text: 'Notera att en kreditprövning görs när du ger ditt medgivande till betalning via Autogiro vilket i vissa fall innebär att en kreditupplysning inhämtas från extern part. Du får då en kopia på kreditupplysningen per fysisk och/eller digital post.',
          },
          {
            title: 'Personuppgifter',
            text: 'Zaver behandlar dina personuppgifter för att uppfylla våra åtaganden enligt detta avtal och för de övriga syften som redovisas i vår Integritetspolicy.'
          },
          {
            title: 'Medgivande till betalning via Autogiro',
            text: `Vid genomförande av transaktionen upprättar du ett autogiromedgivande hos Zaver i enlighet med villkoren för Autogiro.`,
          },
        ],
        links: [
          {
            text: 'Villkoren för Autogiro (pdf)',
            onClick: () => {
              window.open(
                this.directDebitTermsLink,
                '_blank'
              );
            },
          },
        ],
      },
    ];
  }

  getTotalAmount(): string {
    return this.valuePipe.transform(
        this.state.totalCheckoutValue
      )
  }

  async handleCtaClick(): Promise<void> {
    try {
      this.loading = true;
      await this.userService.confirmInstantDebit(this.selectedAccount.id, this.directDebitTermsLink);
      await this.navigate();
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  showPurchaseDetails(): void {
    this.selectedItem = this.selectorItems[0];
    this.toggleModal();
  }

  showTerms(): void {
    this.selectedItem = this.selectorItems[1];
    this.toggleModal();
  }

  changeAccount(): void {
    if (this.accounts && this.accounts.length > 1) {
      this.tempSelectedAccountId = this.selectedAccount?.id;
      this.showAccountSelection = !this.showAccountSelection;
    } else {
      this.selectNewAisAccount();
    }
  }

  onAccountRadioSelect(accountId: string): void {
    this.tempSelectedAccountId = accountId;
  }

  async handleAccountSelectionSubmit(): Promise<void> {
    if (this.tempSelectedAccountId) {
      this.loading = true;
      try {
        await this.aisAccountService.selectAccount(this.tempSelectedAccountId);
        await this.loadAccounts();
        this.showAccountSelection = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  }

  async loadAccounts(): Promise<void> {
    this.accounts = (await this.aisAccountService.getAccounts()).accounts;
    this.selectedAccount = this.accounts.find(acc => acc.isSelected)
      || this.accounts.find(acc => acc.isPrimary)
      || this.accounts[0];
  }

  async selectNewAisAccount(): Promise<void> {
    await this.aisAccountService.resetAccountRequirement();
    this.navigate();
  }

  hideAccountSelection(): void {
      this.showAccountSelection = false;
  }

  getBankDisplayName(bankKey: string): string {
    return BankName[bankKey];
  }
  
  backNavigationAllowed(): boolean {
    const checkoutProducts = [
      ZaverProduct.ECOM_CHECKOUT,
      ZaverProduct.IN_STORE_CHECKOUT
    ];
    return checkoutProducts.includes(this.state.zaverProduct);
  }

  async reroute(): Promise<void> {
    const path: string = this.route.routeConfig.path;

    const controlParams: StateControlParameters = {
      currentPageName: this.stateService.getStepFromUrl(path),
      token: this.stateService.getToken(),
    };
    
    this.state = await this.stateService.reset(controlParams);
    const route = this.stateService.getRouteFromState(this.state);
    this.router.navigate([route], { queryParamsHandling: 'preserve' });
  }
}
